import { currencySymbol, money, moneyRound } from '../../lib/money';

// Wording and number formats of the agent Reports, in one place. Amounts arrive from the server already in the company currency.

export const moneyText = (amount, currency) => (amount == null ? '—' : moneyRound(amount, currency));
export const moneyExact = (amount, currency) => (amount == null ? '—' : money(amount, currency));
export const symbol = (currency) => currencySymbol(currency);

// The big figures of the boards: a million or more reads "$1.92M", anything smaller in full ("$508,320").
export function bigMoney(amount, currency) {
  if (amount == null) return '—';
  const n = Number(amount);
  if (Math.abs(n) < 1000000) return moneyRound(n, currency);
  return `${n < 0 ? '-' : ''}${currencySymbol(currency)}${(Math.abs(n) / 1000000).toFixed(2)}M`;
}

// Names are shown the way people write them: a name that came out of the extraction in CAPITALS ("KRAVET INC. LEE JOFA") is shown in normal capitals, with company
// endings such as LLC and countries such as USA kept in capitals. Display only: the stored name is never changed. A name with any lowercase letter is shown as it is.
const KEEP_UPPER = new Set(['LLC', 'LLP', 'USA', 'UK', 'UAE', 'HK', 'SPA', 'SRL', 'GMBH', 'NV', 'BV', 'SA', 'AG', 'AB', 'SAS', 'SARL', 'PLC', 'LP', 'PTE', 'PVT', 'AS', 'AŞ', 'AS.', 'SDN', 'BHD', 'II', 'III']);
export function displayName(name) {
  const s = name == null ? '' : String(name).trim();
  if (!s || /[a-zà-ÿ]/.test(s) || !/[A-ZÀ-Þ]/.test(s)) return s;
  const word = (w) => {
    const letters = w.replace(/[^A-Za-zÀ-ÿŞşİı]/g, '');
    if (!letters) return w;
    if (KEEP_UPPER.has(letters.toUpperCase()) || (/\./.test(w) && letters.length <= 2) || /^([A-Z]\.){2,}$/.test(w)) return w;
    return w.toLowerCase().replace(/\u0307/g, '').replace(/(^|[^A-Za-zÀ-ÿŞşİı])([a-zà-ÿşı])/g, (m, a, b) => a + b.toUpperCase());
  };
  return s.split(' ').map(word).join(' ');
}

// a supplier / customer as the screens show it: the placeholder Floxir makes for an invoice that names no supplier is "no supplier identified", never a company
export const isPlaceholder = (supplier) => !!(supplier && supplier.unknown);
export const supplierName = (supplier, t) => (!supplier ? t('reports.none') : supplier.unknown ? t('reports.noSupplier') : displayName(supplier.name) || t('reports.none'));
export const customerName = (customer, t) => (customer && displayName(customer.name)) || t('reports.none');
export const countText = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));
export const pctText = (p) => (p == null ? '—' : `${Number(p).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`);
export const signedPct = (p) => (p == null ? '—' : `${p > 0 ? '+' : ''}${Number(p).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`);

const one = (t, base, n, vars = {}) => t(n === 1 ? `${base}.one` : `${base}.many`, { n: countText(n), ...vars });

// "excluding 3 suppliers without a rate", or '' when there are none
export const withoutRateText = (t, n) => (n > 0 ? one(t, 'reports.excludingNoRate', n) : '');

// the notes next to a total: one line per thing the total rests on (never silent, G16 and G17)
export function noteLines(t, notes = {}) {
  const lines = [];
  if (notes.flagged > 0) lines.push(one(t, 'reports.note.flagged', notes.flagged));
  if (notes.no_net > 0) lines.push(one(t, 'reports.note.noNet', notes.no_net));
  if (notes.no_rate_fx > 0) lines.push(one(t, 'reports.note.noFx', notes.no_rate_fx));
  if (notes.assumed_terms > 0) lines.push(one(t, 'reports.note.assumed', notes.assumed_terms));
  if (notes.uncertain_dates > 0) lines.push(one(t, 'reports.note.uncertainDate', notes.uncertain_dates));
  if (notes.future_dated > 0) lines.push(one(t, 'reports.note.future', notes.future_dated));
  if (notes.orders_older_than_window > 0) lines.push(t('reports.note.ordersOlder', { n: countText(notes.orders_older_than_window) }));
  if (notes.orders_value_missing > 0) lines.push(t('reports.note.ordersNoValue', { n: countText(notes.orders_value_missing) }));
  if (notes.orders_future_dated > 0) lines.push(t('reports.note.ordersFuture', { n: countText(notes.orders_future_dated) }));
  return lines;
}

// The label under a bar: weekdays for a week, "Week n" for weeks, the short month for months, day/month for a long run of days.
export function bucketLabel(bucket, grain, index, count, lang, t) {
  const date = new Date(`${bucket.from}T12:00:00Z`);
  if (grain === 'week') return t('reports.week', { n: index + 1 });
  if (grain === 'month') return new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(date);
  if (count <= 7) return new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(date);
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}

// "September 2026" / "2025 (full year)" for the Archive
export function periodLabel(row, lang, t) {
  if (row.kind === 'year') return t('reports.archive.fullYear', { year: row.key });
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${row.from}T12:00:00Z`));
}
